// The handover draft being edited (H-1, H-2, H-Checklist, H-Next): the stored document plus the
// user's changes, kept in memory until the user saves. The same model serves the initial and the
// final handover; the final one also reinspects the initial findings.
import {
  BUSINESS_TIME_ZONE,
  type ContractorSource,
  type FindingStatus,
  GENERAL_CHECK_LIMITS,
  HANDOVER_LIMITS,
  type HandoverDocument,
  type HandoverPhoto,
  type HandoverSaveRequest,
  type InspectionItem,
  type InspectionStatus,
  READING_PATTERN,
  type ReinspectionResult,
  calendarDate,
} from '@sivan/shared';

export interface DraftFinding {
  /** Stable key in the editor; equals `id` for stored findings. */
  readonly key: string;
  readonly id: string | null;
  /**
   * Catalog references and the text shown for them: the recorded text of a stored finding (it
   * does not follow later renames), or the entry's name when the user picks one. A null id with a
   * text is a value recorded before the catalogs existed.
   */
  readonly areaId: string | null;
  readonly area: string;
  readonly componentId: string | null;
  readonly component: string;
  readonly contractorId: string | null;
  readonly contractor: string;
  /** Optional work category (תחום עבודה); filters the contractors offered. */
  readonly workCategoryId: string | null;
  readonly workCategory: string;
  readonly description: string;
  readonly inspectedOn: string;
  /** Photos in display order: stored ones and uploads not saved yet. */
  readonly photos: readonly HandoverPhoto[];
  /** The inspection item the finding is on (its area, category and name are fixed by it). */
  readonly inspectionItemId: string | null;
  /** Open until resolved (fixed) or withdrawn (recorded in error), with a note. */
  readonly status: FindingStatus;
  readonly resolutionNote: string;
  /** How the stored contractor was chosen; the server records it for new choices. */
  readonly contractorSource: ContractorSource | null;
}

export interface DraftMeter {
  readonly key: string;
  readonly id: string | null;
  readonly meterType: string;
  readonly meterNumber: string;
  readonly reading: string;
  readonly note: string;
}

/** A general check's result as edited: the quantity as typed. */
export interface DraftCheck {
  readonly status: InspectionStatus;
  readonly quantity: string;
  readonly note: string;
}

/** The final handover's reinspection of one initial finding. */
export interface DraftReinspection {
  readonly result: ReinspectionResult;
  readonly inspectedOn: string;
  readonly note: string;
  /** New photos, stored with the final handover. */
  readonly photos: readonly HandoverPhoto[];
}

export interface Draft {
  readonly handoverDate: string;
  readonly findings: readonly DraftFinding[];
  readonly meters: readonly DraftMeter[];
  /** Results of the inspection items, by item id. */
  readonly results: Readonly<Record<string, InspectionStatus>>;
  /** General checks, by check id. */
  readonly checks: Readonly<Record<string, DraftCheck>>;
  /** Reinspections (final handover), by initial finding id. */
  readonly reinspections: Readonly<Record<string, DraftReinspection>>;
}

export type FindingFields = Omit<DraftFinding, 'key' | 'id'>;
export type FindingErrors = Partial<Record<keyof FindingFields, string>>;
export type MeterErrors = Partial<Record<'meterType' | 'meterNumber' | 'reading' | 'note', string>>;

/** Meter types offered first; any other type can be typed. */
export const METER_SUGGESTIONS = ['חשמל', 'מים'] as const;

let counter = 0;
/**
 * Unique per page load: a draft restored after a refresh (SAFE-DRAFT) keeps the keys of its new
 * findings, and a finding added afterwards must not reuse one of them.
 */
const LOAD = Math.random().toString(36).slice(2, 8);
export const newKey = (): string => {
  counter += 1;
  return `new-${LOAD}-${String(counter)}`;
};

export function draftFrom(doc: HandoverDocument): Draft {
  return {
    handoverDate: doc.handoverDate ?? '',
    findings: doc.findings.map((f) => ({
      key: f.id,
      id: f.id,
      areaId: f.areaId,
      area: f.area,
      componentId: f.componentId,
      component: f.component,
      contractorId: f.contractorId,
      contractor: f.contractor ?? '',
      workCategoryId: f.workCategoryId,
      workCategory: f.workCategory ?? '',
      description: f.description,
      inspectedOn: f.inspectedOn,
      photos: f.photos,
      inspectionItemId: f.inspectionItemId,
      status: f.status,
      resolutionNote: f.resolutionNote ?? '',
      contractorSource: f.contractorSource,
    })),
    results: Object.fromEntries(doc.inspection.items.map((i) => [i.id, i.status])),
    checks: Object.fromEntries(
      doc.generalChecks.items.map((c) => [
        c.id,
        {
          status: c.status,
          quantity: c.quantity === null ? '' : String(c.quantity),
          note: c.note ?? '',
        },
      ]),
    ),
    reinspections: Object.fromEntries(
      (doc.reinspection?.items ?? []).map((r) => [
        r.finding.id,
        {
          result: r.result,
          inspectedOn: r.inspectedOn ?? '',
          note: r.note ?? '',
          photos: r.photos,
        },
      ]),
    ),
    meters: doc.meters.map((m) => ({
      key: m.id,
      id: m.id,
      meterType: m.meterType,
      meterNumber: m.meterNumber ?? '',
      reading: m.reading ?? '',
      note: m.note ?? '',
    })),
  };
}

const orNull = (text: string): string | null => (text.trim() === '' ? null : text.trim());

const QUANTITY = /^\d{1,4}$/;

export function toSaveRequest(
  draft: Draft,
  rowVersion: number,
  items: HandoverDocument['inspection']['items'] = [],
): HandoverSaveRequest {
  const quantity = (text: string): number | null =>
    QUANTITY.test(text.trim()) ? Number(text.trim()) : null;
  return {
    rowVersion,
    handoverDate: orNull(draft.handoverDate),
    findings: draft.findings.map((f) => ({
      id: f.id,
      areaId: f.areaId,
      componentId: f.componentId,
      contractorId: f.contractorId,
      workCategoryId: f.workCategoryId,
      description: f.description.trim(),
      inspectedOn: f.inspectedOn,
      photoIds: f.photos.map((p) => p.id),
      inspectionItemId: f.inspectionItemId,
      status: f.status,
      resolutionNote: f.status === 'open' ? null : orNull(f.resolutionNote),
    })),
    inspection: items.map((i) => ({ id: i.id, status: draft.results[i.id] ?? i.status })),
    generalChecks: Object.entries(draft.checks).map(([id, c]) => ({
      id,
      status: c.status,
      quantity: quantity(c.quantity),
      note: orNull(c.note),
    })),
    reinspections: Object.entries(draft.reinspections).map(([findingId, r]) => ({
      findingId,
      result: r.result,
      inspectedOn: r.inspectedOn === '' ? null : r.inspectedOn,
      note: orNull(r.note),
      photoIds: r.photos.map((p) => p.id),
    })),
    meters: draft.meters.map((m) => ({
      id: m.id,
      meterType: m.meterType.trim(),
      meterNumber: orNull(m.meterNumber),
      reading: orNull(m.reading),
      note: orNull(m.note),
    })),
  };
}

/** True when saving would change something (the comparison the server makes, on trimmed values). */
export function isDirty(draft: Draft, doc: HandoverDocument): boolean {
  const a = toSaveRequest(draft, doc.rowVersion, doc.inspection.items);
  const b = toSaveRequest(draftFrom(doc), doc.rowVersion, doc.inspection.items);
  const reading = (r: string | null | undefined) => (r == null ? null : String(Number(r)));
  const normalize = (request: HandoverSaveRequest) => ({
    ...request,
    meters: request.meters.map((m) => ({ ...m, reading: reading(m.reading) })),
  });
  return JSON.stringify(normalize(a)) !== JSON.stringify(normalize(b));
}

const tooLong = (text: string, max: number) => text.trim().length > max;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function validateFinding(f: FindingFields): FindingErrors {
  const errors: FindingErrors = {};
  // A catalog entry, or the text of a finding recorded before the catalogs existed.
  if (f.areaId === null && f.area.trim() === '') errors.area = 'יש לבחור אזור';
  if (f.componentId === null && f.component.trim() === '') errors.component = 'יש לבחור רכיב';
  if (f.description.trim() === '') errors.description = 'שדה חובה';
  else if (tooLong(f.description, HANDOVER_LIMITS.description))
    errors.description = `עד ${String(HANDOVER_LIMITS.description)} תווים`;
  if (!ISO_DATE.test(f.inspectedOn)) errors.inspectedOn = 'יש לבחור תאריך';
  if (f.photos.length > HANDOVER_LIMITS.photosPerFinding)
    errors.photos = `עד ${String(HANDOVER_LIMITS.photosPerFinding)} תמונות לליקוי`;
  if (f.status === 'withdrawn' && f.resolutionNote.trim() === '')
    errors.resolutionNote = 'יש לציין מדוע הליקוי נרשם בטעות';
  else if (tooLong(f.resolutionNote, HANDOVER_LIMITS.resolutionNote))
    errors.resolutionNote = `עד ${String(HANDOVER_LIMITS.resolutionNote)} תווים`;
  return errors;
}

/** Open findings of each inspection item. */
export function openFindingsByItem(
  findings: readonly DraftFinding[],
): ReadonlyMap<string, DraftFinding[]> {
  const map = new Map<string, DraftFinding[]>();
  for (const f of findings) {
    if (f.inspectionItemId === null || f.status !== 'open') continue;
    map.set(f.inspectionItemId, [...(map.get(f.inspectionItemId) ?? []), f]);
  }
  return map;
}

/**
 * Closes findings (resolved or withdrawn, with their notes) and sets the result of their items:
 * `target` for the item being marked, and "טרם נבדק" for any other item left with no open finding
 * (it has to be checked again). Returns the items that went back to "טרם נבדק".
 */
export function closeFindings(
  draft: Draft,
  closing: readonly { key: string; status: 'resolved' | 'withdrawn'; note: string }[],
  item: { id: string; target: InspectionStatus } | null,
): { draft: Draft; reopened: string[] } {
  const byKey = new Map(closing.map((c) => [c.key, c]));
  const findings = draft.findings.map((f) => {
    const close = byKey.get(f.key);
    return close === undefined
      ? f
      : { ...f, status: close.status, resolutionNote: close.note.trim() };
  });
  const open = openFindingsByItem(findings);
  const results = { ...draft.results };
  const reopened: string[] = [];
  for (const f of draft.findings) {
    const itemId = f.inspectionItemId;
    if (itemId === null || !byKey.has(f.key) || itemId === item?.id) continue;
    if (!open.has(itemId) && results[itemId] === 'defect') {
      results[itemId] = 'unchecked';
      reopened.push(itemId);
    }
  }
  if (item !== null) results[item.id] = item.target;
  return { draft: { ...draft, findings, results }, reopened: [...new Set(reopened)] };
}

/**
 * Items whose result contradicts their findings (the server refuses the save): "לא תקין" without
 * an open finding, or another result while one is open.
 */
export function inspectionConflicts(draft: Draft): string[] {
  const open = openFindingsByItem(draft.findings);
  return Object.entries(draft.results)
    .filter(([id, status]) => (status === 'defect') !== open.has(id))
    .map(([id]) => id);
}

export function validateMeter(m: DraftMeter): MeterErrors {
  const errors: MeterErrors = {};
  if (m.meterType.trim() === '') errors.meterType = 'שדה חובה';
  else if (tooLong(m.meterType, HANDOVER_LIMITS.meterType))
    errors.meterType = `עד ${String(HANDOVER_LIMITS.meterType)} תווים`;
  if (tooLong(m.meterNumber, HANDOVER_LIMITS.meterNumber))
    errors.meterNumber = `עד ${String(HANDOVER_LIMITS.meterNumber)} תווים`;
  if (m.reading.trim() !== '' && !READING_PATTERN.test(m.reading.trim()))
    errors.reading = 'מספר חיובי, עד 3 ספרות אחרי הנקודה';
  if (tooLong(m.note, HANDOVER_LIMITS.note))
    errors.note = `עד ${String(HANDOVER_LIMITS.note)} תווים`;
  return errors;
}

export type CheckErrors = Partial<Record<'quantity' | 'note', string>>;
export type ReinspectionErrors = Partial<Record<'inspectedOn' | 'note', string>>;

export function validateCheck(c: DraftCheck): CheckErrors {
  const errors: CheckErrors = {};
  if (c.quantity.trim() !== '' && !QUANTITY.test(c.quantity.trim()))
    errors.quantity = `מספר שלם, עד ${String(GENERAL_CHECK_LIMITS.maxQuantity)}`;
  if (tooLong(c.note, GENERAL_CHECK_LIMITS.note))
    errors.note = `עד ${String(GENERAL_CHECK_LIMITS.note)} תווים`;
  return errors;
}

export function validateReinspection(r: DraftReinspection): ReinspectionErrors {
  const errors: ReinspectionErrors = {};
  if (r.result !== 'pending' && !ISO_DATE.test(r.inspectedOn))
    errors.inspectedOn = 'יש לבחור תאריך בדיקה חוזרת';
  if (tooLong(r.note, HANDOVER_LIMITS.reinspectionNote))
    errors.note = `עד ${String(HANDOVER_LIMITS.reinspectionNote)} תווים`;
  return errors;
}

/** Errors of the whole draft, keyed by item key (or check / finding id); empty when it can be saved. */
export function draftErrors(
  draft: Draft,
): Record<string, FindingErrors | MeterErrors | CheckErrors | ReinspectionErrors> {
  const errors: Record<string, FindingErrors | MeterErrors | CheckErrors | ReinspectionErrors> = {};
  for (const [id, c] of Object.entries(draft.checks)) {
    const e = validateCheck(c);
    if (Object.keys(e).length > 0) errors[id] = e;
  }
  for (const [id, r] of Object.entries(draft.reinspections)) {
    const e = validateReinspection(r);
    if (Object.keys(e).length > 0) errors[id] = e;
  }
  for (const f of draft.findings) {
    const e = validateFinding(f);
    if (Object.keys(e).length > 0) errors[f.key] = e;
  }
  for (const m of draft.meters) {
    const e = validateMeter(m);
    if (Object.keys(e).length > 0) errors[m.key] = e;
  }
  return errors;
}

/** Findings grouped by area, groups in order of first appearance, items in draft order. */
export function groupByArea(
  findings: readonly DraftFinding[],
): { area: string; items: DraftFinding[] }[] {
  const groups = new Map<string, DraftFinding[]>();
  for (const f of findings) {
    const area = f.area.trim();
    const list = groups.get(area);
    if (list === undefined) groups.set(area, [f]);
    else list.push(f);
  }
  return [...groups].map(([area, items]) => ({ area, items }));
}

/** Today in Israel (whatever the device's time zone), as YYYY-MM-DD. */
export function today(now = new Date()): string {
  return calendarDate(now, BUSINESS_TIME_ZONE);
}

/** 2026-10-14 → 14/10/2026. */
export function formatDate(iso: string | null): string {
  if (iso === null || !ISO_DATE.test(iso)) return '';
  const [y, m, d] = iso.split('-');
  return `${d ?? ''}/${m ?? ''}/${y ?? ''}`;
}

/**
 * The fields a finding created from an inspection item starts with (DR-189): the item's area, its
 * physical component (or, when it names none, the item's name as the component text, without a
 * catalog link), its work category, and the link to the item. The contractor is not set here: the
 * form suggests one only from a verified default of the work category, and it can be changed.
 */
export function findingFromItem(item: InspectionItem): {
  areaId: string;
  area: string;
  componentId: string | null;
  component: string;
  workCategoryId: string;
  workCategory: string;
  inspectionItemId: string;
} {
  return {
    areaId: item.areaId,
    area: item.area,
    componentId: item.componentId,
    component: item.component ?? item.name,
    workCategoryId: item.workCategoryId,
    workCategory: item.workCategory,
    inspectionItemId: item.id,
  };
}
